/**
 * QRHandoff — desktop-only "scan to continue on your phone" panel.
 *
 * Renders a compact QR code encoding the given URL (preferred: the
 * listing's `spaceship_buy_url`, fallback: the current lander URL).
 * Purpose: a buyer eyeing the deal on desktop can point their phone
 * camera at the QR and land straight in the Spaceship checkout without
 * having to email themselves a link. Zero clicks, no lost intent.
 *
 * Hidden on <900px via the parent CSS class since the mobile buyer is
 * already on their phone.
 */
import { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function QRHandoff({ url, label, sublabel, onScan }) {
  const [copied, setCopied] = useState(false);

  const target = useMemo(() => {
    if (url) return url;
    if (typeof window !== "undefined") return window.location.href;
    return "";
  }, [url]);

  if (!target) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(target);
      setCopied(true);
      toast.success("Link copied — paste it into your phone");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — long-press the link to copy");
    }
  };

  return (
    <div className="cd-qr-handoff" data-testid="qr-handoff">
      <div className="cd-qr-handoff__code" aria-hidden>
        <QRCodeSVG
          value={target}
          size={104}
          bgColor="#ffffff"
          fgColor="#03142f"
          level="M"
          includeMargin={false}
          onClick={onScan}
        />
      </div>
      <div className="cd-qr-handoff__body">
        <div className="cd-qr-handoff__label">
          <Smartphone size={13} strokeWidth={2} />
          <span>{label || "Continue on your phone"}</span>
        </div>
        <p className="cd-qr-handoff__sub">
          {sublabel || "Scan with your camera to open the checkout on mobile."}
        </p>
        <button
          type="button"
          className="cd-qr-handoff__copy"
          onClick={copy}
          data-testid="qr-handoff-copy"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>
    </div>
  );
}
